import { formatDistanceToNow } from 'date-fns';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import type { WebhookLog } from '@/types/api';

interface WebhookLogsListProps {
  logs: WebhookLog[];
  isLoading?: boolean;
  title?: string;
  maxItems?: number;
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'processed':
      return <Badge variant="success">Processed</Badge>;
    case 'received':
      return <Badge variant="default">Received</Badge>;
    case 'failed':
      return <Badge variant="destructive">Failed</Badge>;
    case 'ignored':
      return <Badge variant="secondary">Ignored</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function getEventTypeBadge(eventType: string) {
  // Extract the main event category (e.g., 'listing' from 'listing.new')
  const [category] = eventType.split('.');
  const variant = category === 'listing' ? 'default' : category === 'reservation' ? 'secondary' : 'outline';

  return (
    <Badge variant={variant} className="font-mono text-xs">
      {eventType}
    </Badge>
  );
}

export function WebhookLogsList({
  logs,
  isLoading,
  title = 'Webhook Events',
  maxItems,
}: WebhookLogsListProps): JSX.Element {
  const displayLogs = maxItems ? logs.slice(0, maxItems) : logs;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (logs.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No webhook events received yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <TooltipProvider>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>External ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Duration</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayLogs.map((log) => (
                <TableRow key={log.id} data-testid={`webhook-log-row-${log.id}`}>
                  <TableCell className="whitespace-nowrap">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-default">
                          {formatDistanceToNow(new Date(log.createdAt), { addSuffix: true })}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {new Date(log.createdAt).toLocaleString()}
                      </TooltipContent>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{getEventTypeBadge(log.eventType)}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {log.externalId ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-default truncate max-w-[120px] inline-block">
                            {log.externalId}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{log.externalId}</TooltipContent>
                      </Tooltip>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {log.statusMessage ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-default">{getStatusBadge(log.status)}</span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {log.statusMessage}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      getStatusBadge(log.status)
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {log.processingTimeMs !== null ? `${String(log.processingTimeMs)}ms` : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TooltipProvider>
        {maxItems && logs.length > maxItems && (
          <p className="mt-4 text-sm text-muted-foreground text-center">
            Showing {String(maxItems)} of {String(logs.length)} events
          </p>
        )}
      </CardContent>
    </Card>
  );
}
